import { useState } from "react";
import { TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface MatchPlayFinalizeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueName?: string;
  /** Team Match Play says teams are locked too; Individual speaks only of the roster. */
  isTeamLeague: boolean;
  playerCount: number;
  teamCount: number;
  onSaveDraft: () => Promise<void>;
  onFinalize: () => Promise<void>;
}

/**
 * Finalizing a match play league locks the roster (and, for team leagues, the
 * teams) with no way back to draft, and operators only discover that when they
 * try to add someone. This is the warning that says so before it happens, and
 * offers the draft as the way to keep editing.
 */
export function MatchPlayFinalizeDialog({
  open,
  onOpenChange,
  leagueName,
  isTeamLeague,
  playerCount,
  teamCount,
  onSaveDraft,
  onFinalize,
}: MatchPlayFinalizeDialogProps) {
  const [pending, setPending] = useState<"draft" | "finalize" | null>(null);
  const busy = pending !== null;

  // Both actions report their own failures on the Review step behind this
  // window, so a rejection here only has to not become an unhandled one.
  const run = (kind: "draft" | "finalize", action: () => Promise<void>) => {
    setPending(kind);
    void action()
      .catch((err: unknown) => console.error("finalize warning action", err))
      .finally(() => setPending(null));
  };

  const name = leagueName?.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-[520px] rounded-[18px]"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={busy}
              onClick={() => run("draft", onSaveDraft)}
            >
              {pending === "draft" && <Spinner data-icon="inline-start" />}
              Save Draft
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="rounded-xl"
              disabled={busy}
              onClick={() => run("finalize", onFinalize)}
            >
              {pending === "finalize" && <Spinner data-icon="inline-start" />}
              Finalize League
            </Button>
          </div>
        }
      >
        <DialogHeader className="gap-4 pb-4">
          <span className="flex size-[38px] items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10">
            <TriangleAlert className="size-4 text-amber-600 dark:text-amber-400" />
          </span>
          <DialogTitle className="poppins text-lg leading-snug">
            {name ? `Finalize ${name}?` : "Finalize this league?"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 pb-2">
          <DialogDescription className="text-sm text-text-muted">
            Once a match play league is finalized,{" "}
            <span className="font-semibold text-text-primary">
              {isTeamLeague
                ? "the roster and teams are set"
                : "the roster is set"}
            </span>
            .{" "}
            {isTeamLeague
              ? "You will not be able to add players or change teams."
              : "You will not be able to add players."}
          </DialogDescription>
          <p className="text-sm text-text-muted">
            {isTeamLeague ? "Players and teams" : "Players"} can only be added
            or changed while the league is a draft. Save it as a draft if you
            still need to make changes.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">
              {playerCount} player{playerCount === 1 ? "" : "s"}
            </Badge>
            {isTeamLeague && (
              <Badge variant="outline">
                {teamCount} team{teamCount === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
